/**
 * Panel-left bottom zone — ambient action strip at the foot of the rail.
 *
 * SS-4: the SECTION clipboard tray is retired. PP-0's rule — anything a user
 * can click to insert renders IN the list, never in a tray beside it — now
 * holds for sections: the section list renders the clipboard as a tile at the
 * end of its own rows (section-list.js `_buildClipboardTile`). What remains
 * here is the ELEMENT clipboard tray, which has no list to live in (element
 * paste is context-driven: right-click a target, or Cmd+V on a selection),
 * so a tray with a hint + Clear is still its honest shape.
 */

import { eventBus } from '../core/events.js';
import {
  hasClipboard as hasElementClipboard,
  getClipboard as getElementClipboard,
  clearClipboard as clearElementClipboardStore
} from '../editing/element-clipboard.js';

const ICON_CLOSE =
  '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
const ICON_ELEMENT =
  '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18M3 9h6"/></svg>';

// Human-readable label for the copied element's adapter kind.
const ELEMENT_KIND_LABEL = {
  social: 'Social link',
  'social-link': 'Social link',
  nav: 'Navigation link',
  'nav-link': 'Navigation link',
  header: 'Header element'
};
function elementKindLabel(kind) {
  return ELEMENT_KIND_LABEL[kind] || 'Element';
}

export class PanelBottomZone {
  /**
   * @param {Object} opts
   * @param {HTMLElement} opts.container
   */
  constructor({ container } = {}) {
    this._container = container;
    this._listeners = [];
    this._render();
    this._wireEvents();
    this._refreshElementTray();
  }

  _render() {
    const root = document.createElement('div');
    root.className = 'pb-pbz';

    // Element clipboard tray — a card for copied elements (social / nav
    // links, etc.). The element clipboard is in-tab and not section-scoped,
    // so it shows on every left-panel tab.
    const elTray = document.createElement('div');
    elTray.className = 'pb-pbz-clipboard pb-pbz-clipboard-element';
    elTray.dataset.testid = 'sl-element-clipboard-bar';
    elTray.hidden = true;
    const elSlot = document.createElement('span');
    elSlot.className = 'pb-pbz-clipboard-slot';
    elSlot.dataset.testid = 'sl-element-clipboard-slot';
    elTray.appendChild(elSlot);
    root.appendChild(elTray);

    this._container.appendChild(root);
    this._rootEl = root;
    this._elTrayEl = elTray;
    this._elSlotEl = elSlot;
  }

  _wireEvents() {
    this._listen('clipboard:element-changed', () => this._refreshElementTray());
  }

  _listen(event, fn) {
    eventBus.on(event, fn);
    this._listeners.push([event, fn]);
  }

  _hideElementTray() {
    if (this._elTrayEl) this._elTrayEl.hidden = true;
    if (this._elSlotEl) this._elSlotEl.innerHTML = '';
    this._syncRootVisibility();
  }

  // The root strip is visible when its one remaining tray has content.
  _syncRootVisibility() {
    if (!this._rootEl) return;
    const anyVisible = this._elTrayEl && !this._elTrayEl.hidden;
    this._rootEl.hidden = !anyVisible;
  }

  _refreshElementTray() {
    if (!this._elTrayEl || !this._elSlotEl) return;
    if (!hasElementClipboard()) {
      this._hideElementTray();
      return;
    }
    const clip = getElementClipboard();
    if (!clip) {
      this._hideElementTray();
      return;
    }

    const kindLabel = elementKindLabel(clip.adapterKind);
    this._elSlotEl.innerHTML = '';

    const card = document.createElement('div');
    card.className = 'pb-pbz-clipboard-card pb-pbz-clipboard-card-element';
    card.setAttribute('role', 'group');
    card.setAttribute('aria-label', `Element on clipboard: ${kindLabel}`);

    const thumb = document.createElement('span');
    thumb.className = 'pb-pbz-clipboard-card__thumb pb-pbz-clipboard-card__thumb--fallback';
    thumb.setAttribute('aria-hidden', 'true');
    thumb.innerHTML = ICON_ELEMENT;
    card.appendChild(thumb);

    const labelCol = document.createElement('div');
    labelCol.className = 'pb-pbz-clipboard-card__labels';
    const eyebrow = document.createElement('span');
    eyebrow.className = 'pb-pbz-clipboard-card__eyebrow';
    eyebrow.textContent = 'Element copied';
    const title = document.createElement('span');
    title.className = 'pb-pbz-clipboard-card__title';
    title.textContent = kindLabel;
    title.dataset.tooltip = kindLabel;
    labelCol.append(eyebrow, title);
    card.appendChild(labelCol);

    // Paste for elements is context-driven (right-click a target → Paste, or
    // Cmd+V on a selected element), so the tray offers a hint + Clear rather
    // than a blind paste button.
    const hint = document.createElement('span');
    hint.className = 'pb-pbz-clipboard-card__meta pb-pbz-clipboard-card__hint';
    hint.textContent = 'Right-click a target to paste';
    labelCol.appendChild(hint);

    const actions = document.createElement('div');
    actions.className = 'pb-pbz-clipboard-card__actions';
    const clearBtn = document.createElement('button');
    clearBtn.className = 'pb-pbz-clipboard-card__clear';
    clearBtn.type = 'button';
    clearBtn.setAttribute('aria-label', 'Clear element clipboard');
    clearBtn.setAttribute('data-tooltip', 'Clear element clipboard');
    clearBtn.setAttribute('data-testid', 'sl-element-clipboard-clear');
    clearBtn.innerHTML = ICON_CLOSE;
    clearBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      // Mirror element-actions.clearElementClipboard() without importing it:
      // clear the pure store, then broadcast the same event so this tray (and
      // any future listener) reacts uniformly.
      clearElementClipboardStore();
      eventBus.emit('clipboard:element-changed', { hasClipboard: false, adapterKind: null });
    });
    actions.appendChild(clearBtn);
    card.appendChild(actions);

    this._elSlotEl.appendChild(card);
    this._elTrayEl.hidden = false;
    this._syncRootVisibility();
  }

  destroy() {
    for (const [event, fn] of this._listeners) eventBus.off(event, fn);
    this._listeners = [];
  }
}
